import React, { useContext, useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormGroup, Form, Input, Label, Button, Spinner } from "reactstrap";
import UserContext from "./UserContext";
import ValetApi from "../api/Api";
import styles from "../forms/ValidUpdateProfileForm.module.css";

const ValidRegisterForm = () => {
    const { register } = useContext(UserContext);
    const [locations, setLocations] = useState([]);
    const [podiums, setPodiums] = useState([]);
    // const [roles, setRoles] = useState([]);

    useEffect(() => {
        async function fetchFormData() {
            try {
                const res = await ValetApi.getAllLocations();
                const podiumsRes = await ValetApi.getAllPodiums();
                // const rolesRes = await ValetApi.getAllRoles();
                // setRoles(rolesRes.data.roles);
                setPodiums(podiumsRes.data.podiums);
                setLocations(res.data.locations);
            } catch (err) {
                console.error("Error fetching locations", err);
            }
        }
        fetchFormData();
    }, []);
    // Formik setup
    const formik = useFormik({
        initialValues: {
            username: "",
            password: "",
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            locationId: "",
            podiumId: "",
            roleId: 1, // default to 'Valet' role
        },
        validationSchema: Yup.object({
            username: Yup.string().max(25, "Must be 25 characters or less").required("username required"),
            password: Yup.string().required("password required"),
            firstName: Yup.string().required("firstName required"),
            lastName: Yup.string().required("lastName required"),
            email: Yup.string().required("email required"),
            phone: Yup.string().required("phone required"),
            locationId: Yup.number(),
            podiumId: Yup.number(),
            // roleId: Yup.number().required("roleId is required, select a role"),
        }),
        onSubmit: async (values) => {
            const { username, password, firstName, lastName, email, phone, podiumId } = values;
            await register(username, password, firstName, lastName, email, phone, 1, Number(podiumId));
        },
    });

    const filteredPodiums = formik.values.locationId ? podiums.filter((p) => String(p.locationId) === String(formik.values.locationId)) : [];

    // reset podiumId when locationId changes
    useEffect(() => {
        formik.setFieldValue("podiumId", "");
        // eslint-disable-next-line
    }, [formik.values.locationId]);

    const renderTextField = (val) => (
        <Input
            id={val}
            type="text"
            name={val}
            placeholder={val.toLowerCase()}
            value={formik.values[val]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched[val] && !!formik.errors[val]}
        />
    );

    const renderUsernameField = (val) => (
        <Input
            id={val}
            type="text"
            name={val}
            placeholder={val.toLowerCase()}
            value={formik.values[val]}
            autoComplete="username"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched[val] && !!formik.errors[val]}
        />
    );

    const renderNewPasswordField = (val) => (
        <Input
            id={val}
            type="password"
            name={val}
            placeholder={val.toLowerCase()}
            value={formik.values[val]}
            autoComplete="new-password"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched[val] && !!formik.errors[val]}
        />
    );

    const renderEmailField = (val) => (
        <Input
            id={val}
            type="email"
            name={val}
            placeholder={val.toLowerCase()}
            value={formik.values[val]}
            autoComplete="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched[val] && !!formik.errors[val]}
        />
    );

    const renderPhoneField = (val) => (
        <Input
            id={val}
            type="tel"
            name={val}
            placeholder={val.toLowerCase()}
            value={formik.values[val]}
            autoComplete="tel"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched[val] && !!formik.errors[val]}
        />
    );

    const renderLocationField = (val) =>
        locations.length >= 1 ? (
            <Input
                id={val}
                type="select"
                name={val}
                value={formik.values[val]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched[val] && !!formik.errors[val]}>
                <option value="">Select Location</option>
                {locations.map((location) => (
                    <option
                        key={location.id}
                        value={location.id}>
                        {location.name}
                    </option>
                ))}
            </Input>
        ) : (
            <Spinner
                style={{ height: `3rem`, width: `3rem` }}
                type="grow"
                color="primary"
            />
        );

    const renderPodiumField = (val) =>
        locations.length < 1 ? (
            <Spinner
                style={{ height: `3rem`, width: `3rem` }}
                type="grow"
                color="primary"
            />
        ) : (
            <Input
                id={val}
                type="select"
                name={val}
                value={formik.values[val]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched[val] && !!formik.errors[val]}
                disabled={!formik.values.locationId}>
                <option value="">Select Podium</option>
                {filteredPodiums.map((podium) => (
                    <option
                        key={podium.id}
                        value={podium.id}>
                        {podium.name}
                    </option>
                ))}
            </Input>
        );

    const renderRoleField = (val) => (
        <Input
            id={val}
            type="text"
            name={val}
            value="Valet"
            disabled
        />
    );

    const renderInputField = (val) => {
        switch (val) {
            case "username":
                return renderUsernameField(val);
            case "firstName":
            case "lastName":
                return renderTextField(val);
            case "password":
                return renderNewPasswordField(val);
            case "email":
                return renderEmailField(val);
            case "phone":
                return renderPhoneField(val);
            case "locationId":
                return renderLocationField(val);
            case "podiumId":
                return renderPodiumField(val);
            case "roleId":
                return renderRoleField(val);
            default:
                return renderTextField(val);
        }
    };

    return (
        <section className="page-scroll">
            <div className="page-scroll-inner">
                <div className={styles.formWrapper}>
                    <Form
                        onSubmit={formik.handleSubmit}
                        className={styles.updateForm}>
                        <h3 className={styles.formTitle}>Create Valet Account</h3>
                        {Object.keys(formik.initialValues).map((val) => (
                            <FormGroup
                                key={`FormGroup-${val}`}
                                className={styles.formGroup}>
                                {renderInputField(val)}
                                <Label htmlFor={val}>{val.charAt(0).toUpperCase() + val.slice(1)}</Label>
                                {formik.touched[val] && formik.errors[val] && <div className="text-danger">{formik.errors[val]}</div>}
                            </FormGroup>
                        ))}
                        <Button
                            className={styles.updateBtn}
                            color="primary"
                            type="submit">
                            Register
                        </Button>
                    </Form>
                </div>
            </div>
        </section>
    );
};

export default ValidRegisterForm;
